import { useMemo } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useQuery } from '../hooks/useQuery';
import { useUrlParams } from '../hooks/useUrlState';
import { api, TeamMemberSummary } from '../lib/api';
import {
  ActionButton, SectionLabel, Card, CardHeader, Table, Th, Tr, Td, MetricCard,
  Badge, Avatar, initials, EmptyState, Spinner, UserName,
  PeriodPicker, resolvePeriod, type PeriodPreset,
} from '../components/ui';
import { fmtCurrency } from '../lib/fmt';
import { downloadCsv } from '../lib/csv';

const DEFAULT_PERIOD_PARAMS = { preset: 'last12', from: '', to: '' };

const VISIBLE_TO = new Set(['DM', 'SA', 'DIRECTOR', 'EXEC']);

function fmtMonth(yyyymm: string): string {
  const [y, m] = yyyymm.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleString(undefined, { month: 'short', year: 'numeric' });
}

function memberShippedInMonth(m: TeamMemberSummary, month: string): number {
  return m.periods.find((p) => p.attribution_month === month)?.shipped_value ?? 0;
}

export default function TeamPage() {
  const { user } = useAuth();
  const [periodParams, setPeriodParams] = useUrlParams(DEFAULT_PERIOD_PARAMS);
  const period = resolvePeriod(
    periodParams.preset as PeriodPreset,
    periodParams.from || undefined,
    periodParams.to || undefined,
  );
  const months = period.monthsCount;
  const { data, isLoading, error } = useQuery(
    () => api.team.summary({ months }),
    [user?.jira_user_id, months],
  );

  const sortedMembers = useMemo(() => {
    if (!data) return [];
    const currentMonth = data.rollup[0]?.attribution_month;
    if (!currentMonth) return data.members;
    return [...data.members].sort(
      (a, b) => memberShippedInMonth(b, currentMonth) - memberShippedInMonth(a, currentMonth),
    );
  }, [data]);

  if (!user) return null;
  if (!VISIBLE_TO.has(user.role) && !user.is_admin) {
    return (
      <EmptyState
        message="Team view is for managers (DM/SA), directors, and execs. Your role doesn't have direct reports in this view."
      />
    );
  }

  if (isLoading) return <Spinner />;
  if (error) return <EmptyState message={error} />;
  if (!data) return <EmptyState message="No data" />;

  if (data.member_count === 0) {
    return (
      <EmptyState
        message={`${user.display_name} has no active reports in the org tree. Set the manager field on Users & roles.`}
      />
    );
  }

  const currentMonth = data.rollup[0];
  const priorMonth = data.rollup[1];

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: 10 }}>
        <MetricCard label="Reports (recursive)" value={data.member_count} sub={data.root.title || data.root.role} />
        <MetricCard
          label={`Shipped — ${currentMonth ? fmtMonth(currentMonth.attribution_month) : ''}`}
          value={fmtCurrency(currentMonth?.total_shipped_value ?? 0)}
          accent="green"
          sub={priorMonth ? `vs ${fmtCurrency(priorMonth.total_shipped_value)} prior` : undefined}
        />
        <MetricCard
          label="WIP value (cumulative)"
          value={fmtCurrency(data.total_wip_value)}
          accent="amber"
          sub="Across all unshipped deliverables"
        />
        <MetricCard
          label="Contributing members"
          value={`${currentMonth?.member_count ?? 0} / ${data.member_count}`}
          sub={currentMonth ? `in ${fmtMonth(currentMonth.attribution_month)}` : undefined}
        />
      </div>

      <div>
        <SectionLabel>Subtree roll-up — {months} months</SectionLabel>
        <Card>
          <CardHeader>
            <span style={{ fontSize: 12, fontWeight: 500 }}>Total shipped value across reports</span>
            <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
              <ActionButton
                onClick={() => {
                  const rows: Array<Array<unknown>> = [[
                    'Section', 'Month', 'Shipped value', 'WIP value', 'Contributors',
                    'Member', 'Role', 'Team', 'Title', 'Member shipped', 'Member WIP (cumulative)', 'Tier',
                  ]];
                  // Rollup section
                  for (const r of data.rollup) {
                    rows.push([
                      'Rollup', r.attribution_month,
                      r.total_shipped_value.toFixed(2), r.total_wip_value.toFixed(2),
                      r.member_count, '', '', '', '', '', '', '',
                    ]);
                  }
                  // Members section
                  for (const m of sortedMembers) {
                    const p = currentMonth ? m.periods.find((x) => x.attribution_month === currentMonth.attribution_month) : undefined;
                    rows.push([
                      'Member', currentMonth?.attribution_month ?? '',
                      '', '', '',
                      m.user.display_name, m.user.role, m.user.team, m.user.title,
                      (p?.shipped_value ?? 0).toFixed(2),
                      m.total_wip_value.toFixed(2),
                      p?.bonus_tier ?? '',
                    ]);
                  }
                  downloadCsv(`team-rollup-${user.jira_user_id}.csv`, rows);
                }}
                variant="default"
              >
                Export CSV
              </ActionButton>
              <PeriodPicker
                value={period}
                onChange={(v) => setPeriodParams({
                  preset: v.preset,
                  from: v.preset === 'custom' ? v.from : '',
                  to:   v.preset === 'custom' ? v.to   : '',
                })}
              />
            </span>
          </CardHeader>
          <Table>
            <thead>
              <tr>
                <Th>Month</Th>
                <Th right>Shipped</Th>
                <Th right>WIP</Th>
                <Th right>Contributors</Th>
              </tr>
            </thead>
            <tbody>
              {data.rollup.map((r) => (
                <Tr key={r.attribution_month}>
                  <Td>{fmtMonth(r.attribution_month)}</Td>
                  <Td right bold style={{ color: r.total_shipped_value > 0 ? 'var(--color-green)' : undefined }}>
                    {fmtCurrency(r.total_shipped_value)}
                  </Td>
                  <Td right muted>{fmtCurrency(r.total_wip_value)}</Td>
                  <Td right muted>{r.member_count}</Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </Card>
      </div>

      <div>
        <SectionLabel>
          Members — {currentMonth ? fmtMonth(currentMonth.attribution_month) : 'current period'}
        </SectionLabel>
        <Card>
          <CardHeader>
            <span style={{ fontSize: 12, fontWeight: 500 }}>
              Per-member breakdown ({data.member_count} {data.member_count === 1 ? 'report' : 'reports'})
            </span>
          </CardHeader>
          <Table>
            <thead>
              <tr>
                <Th>Member</Th>
                <Th>Title</Th>
                <Th>Role</Th>
                <Th right>Shipped this month</Th>
                <Th right>WIP (cumulative)</Th>
                <Th>Tier</Th>
              </tr>
            </thead>
            <tbody>
              {sortedMembers.map((m, i) => {
                const period = currentMonth
                  ? m.periods.find((p) => p.attribution_month === currentMonth.attribution_month)
                  : undefined;
                return (
                  <Tr key={m.user.jira_user_id}>
                    <Td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Avatar initials={initials(m.user.display_name)} size={24} index={i} />
                        <div>
                          <div style={{ fontWeight: 500 }}>
                            <UserName name={m.user.display_name} active={m.user.is_active} />
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{m.user.team}</div>
                        </div>
                      </div>
                    </Td>
                    <Td muted>{m.user.title || '—'}</Td>
                    <Td><Badge variant="gray">{m.user.role}</Badge></Td>
                    <Td right bold style={{ color: (period?.shipped_value ?? 0) > 0 ? 'var(--color-green)' : undefined }}>
                      {fmtCurrency(period?.shipped_value ?? 0)}
                    </Td>
                    <Td right muted>{fmtCurrency(m.total_wip_value)}</Td>
                    <Td>
                      {period?.bonus_tier
                        ? <Badge variant="green">Tier {period.bonus_tier}</Badge>
                        : <Badge variant="gray">—</Badge>}
                    </Td>
                  </Tr>
                );
              })}
            </tbody>
          </Table>
        </Card>
      </div>
    </>
  );
}
